import type {
  PortfolioAnalyticsPropertyDto,
  PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { useMainLayout } from '../../layouts/MainPageLayout/MainLayoutContext';
import { isPointVisibleInBound } from '../../../utils/mapUtils/isPointVisibleInBound';
import { DisplayingRegionLevel } from '../../../types/DisplayingRegionLevel';

const getRegionIdKeyForDots = (
  displayMode: DisplayingRegionLevel | undefined,
  property: PortfolioAnalyticsPropertyResponseDto,
): keyof PortfolioAnalyticsPropertyDto | null => {
  switch (displayMode) {
    case DisplayingRegionLevel.COUNTRY:
      return 'stateRegionId';
    case DisplayingRegionLevel.STATE:
      if (!property.propertyDetails.metroStatRegionId) return 'cityRegionId';
      return 'metroStatRegionId';
    default:
      return null;
  }
};

const getPropertiesForPoints = (
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>,
  selectedRegionId: number,
  selectedDisplayingRegionLevel: DisplayingRegionLevel,
) => {
  if (!selectedRegionId) return filteredProperties;
  if (selectedRegionId && selectedDisplayingRegionLevel === DisplayingRegionLevel.STATE)
    return filteredProperties.filter((p) => p.propertyDetails.stateRegionId === Number(selectedRegionId));
  return [];
};

export const useMapAccumulatedDots = () => {
  const {
    filteredProperties,
    selectedRegionId,
    selectedDisplayingRegionLevel,
    displayMode,
    bounds,
    regionIdCenterMapping,
  } = useMainLayout();

  const regionsToRender: Array<{ regionId: number; propertiesCount: number }> = [];

  const propertiesForpoints = getPropertiesForPoints(
    filteredProperties,
    selectedRegionId,
    selectedDisplayingRegionLevel,
  );

  const finalDisplayMode = selectedRegionId ? selectedDisplayingRegionLevel : displayMode;

  propertiesForpoints.forEach((property) => {
    const regionIdKeyForDots = getRegionIdKeyForDots(finalDisplayMode, property);
    if (!regionIdKeyForDots) return;

    const regionId = property.propertyDetails[regionIdKeyForDots] as number;
    const regionCenter = regionIdCenterMapping.get(regionId);
    if (regionCenter) {
      const isRegionCenterVisible = isPointVisibleInBound(regionCenter.center, bounds);
      if (isRegionCenterVisible) {
        if (!regionsToRender.some((r) => r.regionId === regionId)) {
          regionsToRender.push({ regionId, propertiesCount: 1 });
        } else {
          regionsToRender.find((r) => r.regionId === regionId)!.propertiesCount++;
        }
      }
    }
  });

  const points: Array<{ lat: number; lng: number; count: number; regionId: number }> = [];
  regionsToRender.forEach((region) => {
    const regionCenter = regionIdCenterMapping.get(region.regionId)?.center;
    if (regionCenter) {
      points.push({
        lat: regionCenter.lat,
        lng: regionCenter.lng,
        count: region.propertiesCount,
        regionId: region.regionId,
      });
    }
  });

  return points;
};
